/**
 * Enhanced Kanban Drag Ghost - Jira-style drag preview
 * 
 * Ultra-minimal design:
 * - Compact card with just title and item number
 * - Left border for priority color
 * - Fast 180ms drop animation
 * - Original card hidden during drag (opacity-0)
 */

'use client';

import React from 'react';
import { DragOverlay } from '@dnd-kit/core';
import type { WorkItem } from '../UniversalWorkItems';
import { GripVertical } from 'lucide-react';

interface EnhancedDragGhostProps {
  activeItem: WorkItem | null;
  isDragging: boolean;
  children?: React.ReactNode;
}

// Priority color mapping
const PRIORITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  urgent: { bg: 'bg-red-600', border: 'border-red-600', text: 'text-white' },
  critical: { bg: 'bg-red-700', border: 'border-red-700', text: 'text-white' },
  high: { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-white' },
  medium: { bg: 'bg-yellow-500', border: 'border-yellow-600', text: 'text-gray-900' },
  low: { bg: 'bg-green-500', border: 'border-green-500', text: 'text-white' },
};

export function EnhancedDragGhost({ activeItem, isDragging, children: _children }: EnhancedDragGhostProps) {
  if (!activeItem || !isDragging) return null;

  const priority = PRIORITY_COLORS[activeItem.priority || 'medium'] || PRIORITY_COLORS.medium;
  
  // Jira-style: Super clean, minimal preview
  return (
    <DragOverlay 
      dropAnimation={{
        duration: 180,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      }}
    >
      {/* Ultra-compact Jira-style drag preview */}
      <div 
        className={`
          bg-white rounded-md border-l-4 shadow-xl
          w-52 max-w-xs pointer-events-none
          ${priority.border}
        `}
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="p-2.5">
          {/* Title + Item number only */}
          <div className="flex items-start gap-2">
            <GripVertical className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-mono text-gray-400 block mb-0.5">
                {activeItem.item_number}
              </span>
              <h4 className="font-medium text-gray-900 text-xs line-clamp-2 leading-tight">
                {activeItem.title}
              </h4>
            </div>
          </div>
        </div>
      </div>
    </DragOverlay>
  );
}

/**
 * Drop Zone Highlight Component
 * Shows visual feedback when dragging over a valid drop zone
 */
interface DropZoneHighlightProps {
  isOver: boolean;
  canDrop?: boolean;
  children: React.ReactNode;
}

export function DropZoneHighlight({ isOver, canDrop = true, children }: DropZoneHighlightProps) {
  return (
    <div className="relative h-full">
      {/* Highlight overlay - Jira-style blue glow */}
      {isOver && canDrop && (
        <>
          {/* Border highlight */}
          <div className="absolute inset-0 border-2 border-blue-400 rounded-xl pointer-events-none z-10 bg-blue-50/30" 
               style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)' }} />
          {/* Drop indicator line */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-blue-500 rounded-full animate-pulse z-10" />
        </>
      )}
      {isOver && !canDrop && (
        <div className="absolute inset-0 bg-red-500/10 border-2 border-red-400 border-dashed rounded-xl pointer-events-none z-10">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
              Cannot drop here
            </div>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Card Drag Handle Component
 * Visual indicator for draggable cards - appears on hover
 */
export function CardDragHandle({ className = '' }: { className?: string }) {
  return (
    <div 
      className={`
        opacity-0 group-hover:opacity-100 transition-opacity duration-200
        cursor-grab active:cursor-grabbing
        ${className}
      `}
    >
      <div className="p-1.5 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 shadow-sm transition-colors">
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  );
}

/**
 * Placeholder Component
 * Shows where the card will be dropped
 */
interface DropPlaceholderProps {
  height?: number;
}

export function DropPlaceholder({ height = 120 }: DropPlaceholderProps) {
  return (
    <div 
      className="border-2 border-dashed border-blue-300 rounded-xl bg-blue-50/50 transition-all duration-200"
      style={{ height: `${height}px` }}
    >
      <div className="h-full flex items-center justify-center text-blue-400 text-sm font-medium">
        Drop here
      </div>
    </div>
  );
}
